import map from 'lodash/map'
import React from 'react'
import { Badge, Col } from 'react-bootstrap'
import type { OptionProps } from 'react-select'
import { components } from 'react-select'
import type { Chat } from '@ztechapps/common.contract'
import type { DataLoadOptions, FieldEditorInjectedProps, SelectAsyncProps, SelectOption } from '@ztechapps/webui.input'
import { buildConversationLabel, documentLinkKinds, documentLinkLabels } from '@ztechapps/chat.common'
import { LocalDate } from '@ztechapps/webui.date'
import { SelectAsync } from '@ztechapps/webui.input'
import { HorizontalStack } from '@ztechapps/webui.util'
import { ChatStatus } from '../Conversation/ChatStatusBadge'
import { conversationInboxQuery } from '../Inbox/graphql'
import { buildConversationFilter } from './buildConversationFilter'
import { conversationSelectOptions } from './conversationSelectOptions'

export type ConversationSelectProps = SelectAsyncProps & Pick<FieldEditorInjectedProps, 'form' | 'meta'>

type ConversationOption = SelectOption & Chat

const Option = ({ data, isDisabled, ...rest }: OptionProps<ConversationOption>) => {
  const linkKinds = documentLinkKinds(data)

  //always set the isSelected to false so it wont highlight
  return (
    <components.Option {...rest} data={data} isDisabled={isDisabled} isSelected={false}>
      {/* A disabled option (linked to another record) is dimmed — the select's option style keeps the body colour either way. */}
      <HorizontalStack gap={2} className={isDisabled ? 'opacity-50' : undefined}>
        <div className="flex-shrink-0 text-nowrap">
          <HorizontalStack gap={0}>
            <ChatStatus status={data?.status} />
            <div className="fw-bold">{data?.code}</div>
          </HorizontalStack>
        </div>

        <Col className="text-truncate">
          <small>{data?.subject}</small>
        </Col>

        <Col xs="auto" className="flex-shrink-1 text-truncate text-body-secondary" style={{ maxWidth: '35%' }}>
          <small>{data?.sender?.company || '--'}</small>
        </Col>

        {/* The linked document: its kind from `metadata`, its code from `document` — the same pair the
            details rail's Related document row reads. A conversation carries one document, so its code
            sits on the first kind, as InboxDocumentCell picks it. */}
        {map(linkKinds, (kind, index) => (
          <Badge key={kind} bg="secondary-subtle" text="secondary-emphasis" className="flex-shrink-0 fw-normal">
            {documentLinkLabels[kind]}
            {index === 0 && data?.document?.code ? ` ${data.document.code}` : ''}
          </Badge>
        ))}
        <div className="flex-shrink-0 text-nowrap text-body-secondary">
          <small>
            <LocalDate value={data?.lastMessageAt} />
          </small>
        </div>
      </HorizontalStack>
    </components.Option>
  )
}

// `mergeStyle` lodash-merges a caller `option` over the shared one, so this replaces it whole: the shared
// focus highlight, minus the highlight and the pointer on a disabled row (it cannot be picked).
const styles = {
  option: (provided: any, { isFocused, isDisabled }: { isFocused: boolean; isDisabled: boolean }) => ({
    ...provided,
    color: 'var(--bs-body-color)',
    backgroundColor: isFocused && !isDisabled ? 'var(--bs-warning-bg-subtle)' : '',
    cursor: isDisabled ? 'not-allowed' : provided.cursor,
  }),
}

export const ConversationSelect = ({
  filter,
  dataLoadOptions,
  form: _form,
  meta: _meta,
  ...rest
}: ConversationSelectProps) => (
  <SelectAsync
    placeholder="Type conversation code, subject or sender."
    noOptionsMessage={() => 'Cannot find any conversations.'}
    {...rest}
    components={{ Option }}
    styles={styles}
    dataLoadOptions={
      dataLoadOptions ??
      ({
        // `query` is read at runtime by setupLoadOptions but absent from the lib type.
        query: conversationInboxQuery,
        resultFieldName: 'allChat',
        // `input` is the typed search text at runtime; the lib types it loosely as `Record<any, any>`.
        filter: ({ input }) => buildConversationFilter(input as unknown as string, filter),
        options: conversationSelectOptions,
      } as DataLoadOptions)
    }
    getOptionLabel={(option) => buildConversationLabel(option?.code, option?.subject, option?.sender?.company)}
  />
)
